import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Minus, Plus, Check, Sparkles, Truck, Store } from "lucide-react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import Header from "../components/Header";
import IngredientCard from "../components/IngredientCard";
import NoneOption from "../components/NoneOption";
import MagicModal from "../components/MagicModal";
import Cart from "../components/Cart";
import { computePrice } from "../lib/pricing";
import { US_STATES, computeShipping } from "../lib/shipping";
import { computeNutrition, detectAllergens } from "../lib/nutrition";
import {
  STYLES, FRUITS, CREAMS, CRAZY, CRUNCHY, SWIRLS, SPICY, ALCOHOLS, FUNCTIONAL, PICKUP_LOCATIONS,
} from "../lib/options";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOTAL_STEPS = 11;

const toggleInArray = (arr, item) =>
  arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];

// Mutually exclusive gelato styles
const STYLE_CONFLICTS = {
  dairy: ["sorbet", "vegan", "lactose-free"],
  sorbet: ["dairy"],
  vegan: ["dairy"],
  "lactose-free": ["dairy"],
};
const STYLE_LABELS = STYLES.reduce((acc, s) => ({ ...acc, [s.id]: s.label }), {});


const slide = {
  initial: { opacity: 0, x: 30 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -30 },
  transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
};

export default function CreateGelato() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [magicOpen, setMagicOpen] = useState(false);
  const [functionalOpen, setFunctionalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const [state, setState] = useState({
    quantity_kg: 3,
    styles: [],
    fruits: [],
    creams: [],
    crazy: [],
    crunchy: [],
    swirls: [],
    spicy: [],
    alcohol_type: "",
    alcohol_percent: 0,
    functional: [],
    gelato_name: "",
    customer: {
      full_name: "",
      email: "",
      phone: "",
      fulfillment: "delivery",
      shipping_address: "",
      shipping_city: "",
      shipping_state: "",
      shipping_zip: "",
      pickup_location: PICKUP_LOCATIONS[0],
      notes: "",
    },
  });

  const update = (k, v) => setState((s) => ({ ...s, [k]: v }));
  const updateCustomer = (k, v) =>
    setState((s) => ({ ...s, customer: { ...s.customer, [k]: v } }));

  const removeCartItem = (item) => {
    if (!item.key) return;
    if (item.key === "alcohol_type") {
      setState((s) => ({ ...s, alcohol_type: "", alcohol_percent: 0 }));
      return;
    }
    setState((s) => ({ ...s, [item.key]: (s[item.key] || []).filter((x) => x !== item.label) }));
  };

  const goNext = () => {
    if (step === 2) {
      if (state.styles.length === 0) {
        toast.error("Please choose at least one gelato style to continue");
        return;
      }
      // Open magic modal between step 2 and step 3
      setMagicOpen(true);
      return;
    }
    if (step === 3) {
      const totalFlavors =
        state.fruits.length + state.creams.length + state.crazy.length;
      if (totalFlavors === 0) {
        toast.error("Please choose at least one flavor (fruit, cream, or something crazy) to continue");
        return;
      }
    }
    if (step === 5) {
      // Step 6 alcohol; step 7 functional modal opens after step 6
    }
    if (step === 7) {
      setFunctionalOpen(true);
      return;
    }
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };
  const goPrev = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!state.gelato_name.trim()) {
      toast.error("Please name your gelato first");
      setStep(9);
      return;
    }
    const c = state.customer;
    if (!c.full_name || !c.email || !c.phone) {
      toast.error("Please fill in all required customer details");
      return;
    }
    let shippingCost = 0;
    if (c.fulfillment === "delivery") {
      if (!c.shipping_address.trim() || !c.shipping_city.trim() || !c.shipping_state || !c.shipping_zip.trim()) {
        toast.error("Please provide your address, city, state and ZIP code");
        return;
      }
      const ship = computeShipping(c.shipping_state, c.shipping_zip);
      if (ship.blocked) {
        toast.error(ship.message);
        return;
      }
      shippingCost = ship.cost;
    }
    setSubmitting(true);
    try {
      const { total } = computePrice(state);
      const payload = { ...state, price_usd: total, shipping_cost: shippingCost };
      const { data } = await axios.post(`${API}/gelato/requests`, payload);
      setConfirmed(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return <Confirmation data={confirmed} onHome={() => navigate("/")} />;
  }

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: "#B8C5CD" }}>
      <Toaster position="top-center" richColors />
      <Header />
      <Cart state={state} onRemove={removeCartItem} onGoToStep={setStep} />

      {/* Step indicator */}
      <div className="max-w-3xl mx-auto px-6 pt-8 pb-2">
        <div className="flex items-center justify-between mb-3">
          <div
            className="font-sans"
            style={{
              fontSize: "14px",
              fontWeight: 700,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: "#8E6E3F",
            }}
          >
            Step {step} / {TOTAL_STEPS}
          </div>
          {step > 1 && (
            <button
              data-testid="back-btn"
              onClick={goPrev}
              className="inline-flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded-full transition"
              style={{ color: "#2C2A28" }}
            >
              <ArrowLeft size={14} /> Back
            </button>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <div
              key={i}
              data-testid={`step-dot-${i + 1}`}
              className="flex-1 rounded-full transition-all duration-500"
              style={{
                height: i + 1 <= step ? "10px" : "8px",
                backgroundColor: i + 1 <= step ? "#8E6E3F" : "rgba(184,153,104,0.28)",
                boxShadow: i + 1 <= step ? "0 1px 4px rgba(142,110,63,0.35)" : "none",
              }}
            />
          ))}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 pt-10">
        <AnimatePresence mode="wait">
          <motion.div key={step} {...slide}>
            {step === 1 && <StepQuantity value={state.quantity_kg} onChange={(v) => update("quantity_kg", v)} />}
            {step === 2 && <StepStyle value={state.styles} onChange={(v) => update("styles", v)} />}
            {step === 3 && <StepFlavors state={state} update={update} />}
            {step === 4 && <StepCrunchy value={state.crunchy} onChange={(v) => update("crunchy", v)} />}
            {step === 5 && <StepSwirls value={state.swirls} onChange={(v) => update("swirls", v)} />}
            {step === 6 && <StepSpicy value={state.spicy} onChange={(v) => update("spicy", v)} />}
            {step === 7 && (
              <StepAlcohol
                type={state.alcohol_type}
                percent={state.alcohol_percent}
                setType={(v) => update("alcohol_type", v)}
                setPercent={(v) => update("alcohol_percent", v)}
              />
            )}
            {step === 8 && <StepFunctional value={state.functional} onChange={(v) => update("functional", v)} />}
            {step === 9 && <StepName value={state.gelato_name} onChange={(v) => update("gelato_name", v)} />}
            {step === 10 && <StepReview state={state} goTo={setStep} />}
            {step === 11 && <StepCustomer state={state.customer} update={updateCustomer} />}
          </motion.div>
        </AnimatePresence>

        <div className="mt-12">
          {step === 11 && (
            <p className="text-xs text-center mb-5 mx-auto" style={{ color: "#75716C", maxWidth: "560px" }}>
              Your custom gelato will be prepared only after payment is completed. You will receive a secure payment link by email or text.
            </p>
          )}
          <div className="flex justify-center">
            {step < TOTAL_STEPS ? (
              <button
                data-testid="next-btn"
                onClick={goNext}
                className="inline-flex items-center gap-3 px-8 py-3.5 rounded-full font-medium transition-all duration-300"
                style={{ backgroundColor: "#2C2A28", color: "#B8C5CD" }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#B89968")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#2C2A28")}
              >
                Continue <ArrowRight size={16} />
              </button>
            ) : (
              <button
                data-testid="submit-btn"
                onClick={submit}
                disabled={submitting}
                className="inline-flex items-center gap-3 px-8 py-3.5 rounded-full font-medium transition-all duration-300"
                style={{ backgroundColor: "#9CAF88", color: "#B8C5CD", opacity: submitting ? 0.6 : 1 }}
              >
                {submitting ? "Sending…" : "Submit My Custom Gelato"} <Check size={16} />
              </button>
            )}
          </div>
        </div>
      </div>

      <MagicModal
        open={magicOpen}
        onClose={() => { setMagicOpen(false); setStep(3); }}
        title="Let the magic begin."
        subtitle="Now choose your flavor base — fruits, creams, or both."
        testId="magic-modal"
      >
        <button
          data-testid="magic-continue-btn"
          onClick={() => { setMagicOpen(false); setStep(3); }}
          className="px-8 py-3 rounded-full font-medium"
          style={{ backgroundColor: "#A8BCC8", color: "#2C2A28" }}
        >
          Begin
        </button>
      </MagicModal>

      <MagicModal
        open={functionalOpen}
        onClose={() => { setFunctionalOpen(false); setStep(8); }}
        title="Make your gelato functional."
        subtitle="What should your gelato do for you?"
        testId="functional-modal"
      >
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            data-testid="functional-continue-btn"
            onClick={() => { setFunctionalOpen(false); setStep(8); }}
            className="px-8 py-3 rounded-full font-medium transition"
            style={{ backgroundColor: "#9CAF88", color: "#F0E6D2" }}
          >
            Choose
          </button>
          <button
            data-testid="functional-skip-btn"
            onClick={() => {
              update("functional", []);
              setFunctionalOpen(false);
              setStep(9);
            }}
            className="px-8 py-3 rounded-full font-medium transition border"
            style={{ backgroundColor: "transparent", color: "#2C2A28", borderColor: "rgba(184,153,104,0.4)" }}
          >
            Skip
          </button>
        </div>
      </MagicModal>
    </div>
  );
}

/* ---------- Steps ---------- */

const StepHeader = ({ eyebrow, title, subtitle }) => (
  <div className="mb-8">
    <div className="eyebrow mb-3">{eyebrow}</div>
    <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight leading-tight" style={{ color: "#2C2A28" }}>
      {title}
    </h1>
    {subtitle && <p className="mt-3 text-base" style={{ color: "#4A4744" }}>{subtitle}</p>}
  </div>
);

function StepQuantity({ value, onChange }) {
  const inc = () => onChange(Math.min(8, value + 1));
  const dec = () => onChange(Math.max(3, value - 1));
  return (
    <div>
      <StepHeader eyebrow="Step 1 · Quantity" title="How many kilograms would you like?" subtitle="Minimum 3 kg · Maximum 8 kg" />
      <div className="flex items-center justify-center gap-6 py-12 rounded-3xl border" style={{ backgroundColor: "#FFFFFF", borderColor: "rgba(184,153,104,0.2)" }}>
        <button data-testid="qty-dec-btn" onClick={dec} className="w-14 h-14 rounded-full flex items-center justify-center transition" style={{ backgroundColor: "#B8C5CD", color: "#2C2A28" }}>
          <Minus size={18} />
        </button>
        <div data-testid="qty-value" className="font-serif-display text-7xl tracking-tight" style={{ color: "#2C2A28" }}>
          {value}<span className="text-3xl ml-2" style={{ color: "#B89968" }}>kg</span>
        </div>
        <button data-testid="qty-inc-btn" onClick={inc} className="w-14 h-14 rounded-full flex items-center justify-center transition" style={{ backgroundColor: "#A8BCC8", color: "#2C2A28" }}>
          <Plus size={18} />
        </button>
      </div>
    </div>
  );
}

function StepStyle({ value, onChange }) {
  const handleToggle = (id) => {
    if (value.includes(id)) {
      onChange(value.filter((x) => x !== id));
      return;
    }
    const conflicts = STYLE_CONFLICTS[id] || [];
    const removed = value.filter((x) => conflicts.includes(x));
    const kept = value.filter((x) => !conflicts.includes(x));
    onChange([...kept, id]);
    if (removed.length) {
      const names = removed.map((r) => STYLE_LABELS[r]).join(", ");
      toast.info(`${STYLE_LABELS[id]} non è compatibile con ${names}. Selezione aggiornata.`);
    }
  };
  return (
    <div>
      <StepHeader eyebrow="Step 2 · Style" title="Choose your gelato style." subtitle="Select any that apply." />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {STYLES.map((s) => (
          <IngredientCard
            key={s.id}
            label={s.label}
            selected={value.includes(s.id)}
            onClick={() => handleToggle(s.id)}
            testId={`style-${s.id}`}
          />
        ))}
      </div>
    </div>
  );
}

function StepFlavors({ state, update }) {
  const [tab, setTab] = useState("fruits");
  return (
    <div>
      <StepHeader eyebrow="Step 3 · Flavors" title="Fruits, creams, or something crazy?" subtitle="Switch tabs anytime — your selections are saved." />
      <div className="flex gap-3 mb-8 flex-wrap">
        {["fruits", "creams", "crazy"].map((t) => (
          <button
            key={t}
            data-testid={`tab-${t}`}
            onClick={() => setTab(t)}
            className="rounded-full capitalize transition"
            style={{
              padding: "16px 32px",
              fontSize: "17px",
              fontWeight: 700,
              letterSpacing: "0.02em",
              backgroundColor: tab === t ? "#2C2A28" : "transparent",
              color: tab === t ? "#F0E6D2" : "#2C2A28",
              border: tab === t ? "none" : "2px solid rgba(184,153,104,0.45)",
              boxShadow: tab === t ? "0 6px 20px rgba(44,42,40,0.25)" : "none",
            }}
          >
            {t === "crazy" ? "Something Crazy" : t}
            {state[t].length > 0 && (
              <span className="ml-2" style={{ fontSize: "15px", opacity: 0.85, fontWeight: 600 }}>
                ({state[t].length})
              </span>
            )}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
        <NoneOption
          selected={state[tab].length === 0}
          onClick={() => update(tab, [])}
          testId={`${tab}-none`}
          label={`No ${tab === "crazy" ? "crazy" : tab}`}
        />
        {(tab === "fruits" ? FRUITS : tab === "creams" ? CREAMS : CRAZY).map((item) => (
          <IngredientCard
            key={item}
            label={item}
            selected={state[tab].includes(item)}
            onClick={() => update(tab, toggleInArray(state[tab], item))}
            testId={`${tab}-${item.toLowerCase().replace(/\s|%/g, "-")}`}
          />
        ))}
      </div>
    </div>
  );
}

function StepCrunchy({ value, onChange }) {
  return (
    <div>
      <StepHeader eyebrow="Step 4 · Texture" title="Add something crunchy." subtitle="Optional — pick as many as you like." />
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
        <NoneOption
          selected={value.length === 0}
          onClick={() => onChange([])}
          testId="crunchy-none"
        />
        {CRUNCHY.map((c) => (
          <IngredientCard key={c} label={c} selected={value.includes(c)} onClick={() => onChange(toggleInArray(value, c))} testId={`crunchy-${c.toLowerCase().replace(/\s/g, "-")}`} />
        ))}
      </div>
    </div>
  );
}

function StepSwirls({ value, onChange }) {
  return (
    <div>
      <StepHeader eyebrow="Step 5 · Variegato" title="Choose your swirl." subtitle="Beautiful ribbons of flavor through the gelato." />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <NoneOption
          selected={value.length === 0}
          onClick={() => onChange([])}
          testId="swirl-none"
        />
        {SWIRLS.map((s) => (
          <IngredientCard key={s} label={s} selected={value.includes(s)} onClick={() => onChange(toggleInArray(value, s))} testId={`swirl-${s.toLowerCase().replace(/\s/g, "-")}`} />
        ))}
      </div>
    </div>
  );
}

function StepSpicy({ value, onChange }) {
  return (
    <div>
      <StepHeader eyebrow="Step 6 · Spicy" title="A touch of heat?" subtitle="Optional — spices that wake up the palate." />
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
        <NoneOption selected={value.length === 0} onClick={() => onChange([])} testId="spicy-none" />
        {SPICY.map((s) => (
          <IngredientCard key={s} label={s} selected={value.includes(s)} onClick={() => onChange(toggleInArray(value, s))} testId={`spicy-${s.toLowerCase().replace(/\s/g, "-")}`} />
        ))}
      </div>
    </div>
  );
}


function StepAlcohol({ type, percent, setType, setPercent }) {  return (
    <div>
      <StepHeader eyebrow="Step 7 · Spirit" title="Would you like to add alcohol?" subtitle="Optional — a refined finish." />
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8">
        <IngredientCard label="None" selected={!type} onClick={() => { setType(""); setPercent(0); }} testId="alcohol-none" />
        {ALCOHOLS.map((a) => (
          <IngredientCard key={a} label={a} selected={type === a} onClick={() => { setType(a); if (percent === 0) setPercent(1.0); }} testId={`alcohol-${a.toLowerCase()}`} />
        ))}
      </div>
      {type && (
        <div className="bg-white rounded-2xl p-7 border" style={{ borderColor: "rgba(184,153,104,0.2)" }}>
          <div className="flex items-baseline justify-between mb-4">
            <span className="eyebrow">Alcohol percentage</span>
            <span data-testid="alcohol-percent-value" className="font-serif-display text-3xl" style={{ color: "#2C2A28" }}>
              {percent.toFixed(1)}<span className="text-base ml-1" style={{ color: "#B89968" }}>%</span>
            </span>
          </div>
          <input
            data-testid="alcohol-slider"
            type="range"
            min="0.1"
            max="4.9"
            step="0.1"
            value={percent}
            onChange={(e) => setPercent(parseFloat(e.target.value))}
            className="sc-slider"
          />
          <div className="flex justify-between text-xs mt-2" style={{ color: "#75716C" }}>
            <span>0.1%</span>
            <span>4.9%</span>
          </div>
        </div>
      )}
    </div>
  );
}

function StepFunctional({ value, onChange }) {
  return (
    <div>
      <StepHeader eyebrow="Step 8 · Functional" title="What should your gelato do for you?" subtitle="Optional — choose any benefits to infuse." />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          data-testid="functional-none"
          onClick={() => onChange([])}
          className={`sc-card ${value.length === 0 ? "selected" : ""} flex items-start gap-4 p-5 text-left`}
        >
          <Sparkles size={22} strokeWidth={1.4} style={{ color: value.length === 0 ? "#2C2A28" : "#B89968", flexShrink: 0, marginTop: 2 }} />
          <div>
            <div className="font-serif-display text-xl" style={{ color: "#2C2A28" }}>None</div>
            <div className="text-sm mt-0.5" style={{ color: "#4A4744" }}>Just the pure pleasure</div>
          </div>
        </button>
        {FUNCTIONAL.map((f) => {
          const selected = value.includes(f.id);
          return (
            <button
              key={f.id}
              data-testid={`functional-${f.id}`}
              onClick={() => onChange(toggleInArray(value, f.id))}
              className={`sc-card ${selected ? "selected" : ""} flex items-start gap-4 p-5 text-left`}
            >
              <Sparkles size={22} strokeWidth={1.4} style={{ color: selected ? "#2C2A28" : "#9CAF88", flexShrink: 0, marginTop: 2 }} />
              <div>
                <div className="font-serif-display text-xl" style={{ color: "#2C2A28" }}>{f.label}</div>
                <div className="text-sm mt-0.5" style={{ color: selected ? "#2C2A28" : "#4A4744" }}>{f.desc}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepReview({ state, goTo }) {
  const { items, total } = computePrice(state);
  const groups = items.reduce((acc, it) => {
    (acc[it.category] = acc[it.category] || []).push(it);
    return acc;
  }, {});
  const stepMap = {
    Quantity: 1,
    Fruit: 3,
    Cream: 3,
    Crazy: 3,
    Crunchy: 4,
    Swirl: 5,
    Spice: 6,
    Alcohol: 7,
    Functional: 8,
  };
  return (
    <div>
      <StepHeader
        eyebrow="Step 10 · Review"
        title="Confirm your creation."
        subtitle="Check ingredients and final price. You can still go back and edit any section."
      />
      <div
        className="rounded-3xl p-6 md:p-8 border"
        style={{ backgroundColor: "#FFFFFF", borderColor: "rgba(184,153,104,0.3)" }}
      >
        <div className="flex items-baseline justify-between pb-5 mb-5 border-b" style={{ borderColor: "rgba(184,153,104,0.25)" }}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: "#8E6E3F" }}>Total</div>
            <div className="font-serif-display text-5xl mt-1" style={{ color: "#2C2A28" }}>${total}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: "#8E6E3F" }}>Name</div>
            <div className="font-serif-display text-2xl italic mt-1" style={{ color: "#2C2A28" }}>{state.gelato_name || "—"}</div>
          </div>
        </div>
        {state.styles.length > 0 && (
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "#8E6E3F" }}>Style</div>
              <button data-testid="review-edit-style" onClick={() => goTo(2)} className="text-xs font-semibold underline" style={{ color: "#8E6E3F" }}>Edit</button>
            </div>
            <div className="text-sm" style={{ color: "#2C2A28" }}>{state.styles.join(" · ")} <span style={{ color: "#75716C" }}>(included)</span></div>
          </div>
        )}
        {Object.entries(groups).map(([cat, list]) => (
          <div key={cat} className="mb-4">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "#8E6E3F" }}>{cat}</div>
              {stepMap[cat] && (
                <button data-testid={`review-edit-${cat.toLowerCase()}`} onClick={() => goTo(stepMap[cat])} className="text-xs font-semibold underline" style={{ color: "#8E6E3F" }}>Edit</button>
              )}
            </div>
            {list.map((it) => (
              <div key={it.label} className="flex justify-between text-sm py-1" style={{ color: "#2C2A28" }}>
                <span>{it.label}</span>
                <span className="font-semibold">${it.price}</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      <NutritionAllergens state={state} />

      <div className="mt-6 p-5 rounded-2xl border-l-4" style={{ backgroundColor: "#F5EFE0", borderColor: "#8E6E3F" }}>
        <p className="text-sm leading-relaxed font-medium" style={{ color: "#2C2A28" }}>
          By clicking Continue, you confirm the ingredients and the price of <strong>${total}</strong>. The Santa Chiara team will review and send you a secure payment link.
        </p>
      </div>
    </div>
  );
}

function NutritionAllergens({ state }) {
  const n = computeNutrition(state);
  const { allergens, alcoholNote } = detectAllergens(state);

  const rows = [
    { label: "Energy", value: `${n.energy_kj} kJ / ${n.energy_kcal} kcal` },
    { label: "Fat", value: `${n.fat_g} g` },
    { label: "of which saturates", value: `${n.saturated_g} g`, indent: true },
    { label: "Carbohydrate", value: `${n.carbs_g} g` },
    { label: "of which sugars", value: `${n.sugars_g} g`, indent: true },
    { label: "Protein", value: `${n.protein_g} g` },
    { label: "Salt", value: `${n.salt_g} g` },
  ];

  return (
    <div
      data-testid="nutrition-allergens-box"
      className="mt-6 rounded-3xl p-6 md:p-8 border"
      style={{ backgroundColor: "#FFFFFF", borderColor: "rgba(184,153,104,0.3)" }}
    >
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1" style={{ color: "#8E6E3F" }}>
        Nutritional values
      </div>
      <div className="text-xs mb-4" style={{ color: "#75716C" }}>
        Average values per 100g · {n.label}
      </div>

      <table className="w-full" data-testid="nutrition-table">
        <tbody>
          {rows.map((r) => (
            <tr key={r.label} style={{ borderBottom: "1px solid rgba(184,153,104,0.18)" }}>
              <td
                className="py-2 text-sm"
                style={{ color: r.indent ? "#75716C" : "#2C2A28", paddingLeft: r.indent ? "16px" : 0 }}
              >
                {r.label}
              </td>
              <td className="py-2 text-sm text-right font-semibold" style={{ color: "#2C2A28" }}>
                {r.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-2" style={{ color: "#8E6E3F" }}>
          Allergens
        </div>
        {allergens.length > 0 ? (
          <div className="flex flex-wrap gap-2" data-testid="allergens-list">
            {allergens.map((a) => (
              <span
                key={a}
                data-testid={`allergen-${a.toLowerCase().replace(/\s/g, "-")}`}
                className="text-xs font-medium px-3 py-1 rounded-full"
                style={{ backgroundColor: "#F5EFE0", color: "#7B1E3B", border: "1px solid rgba(123,30,59,0.25)" }}
              >
                {a}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm" style={{ color: "#75716C" }} data-testid="allergens-none">
            No major allergens detected from your selections.
          </p>
        )}
        {alcoholNote && (
          <p className="mt-3 text-sm font-medium" style={{ color: "#7B1E3B" }} data-testid="alcohol-note">
            {alcoholNote}
          </p>
        )}
        <p className="mt-4 text-xs leading-relaxed" style={{ color: "#75716C" }}>
          May contain traces of milk, eggs, tree nuts, peanuts, soy, sesame and gluten due to shared preparation equipment.
        </p>
      </div>
    </div>
  );
}

function StepName({ value, onChange }) {
  return (
    <div>
      <StepHeader eyebrow="Step 9 · Identity" title="Name your gelato." subtitle="Every masterpiece deserves a name." />
      <input
        data-testid="gelato-name-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. Notte Stellata"
        className="w-full bg-white border rounded-2xl px-6 py-5 text-2xl font-serif-display italic outline-none transition focus:ring-2"
        style={{ borderColor: "rgba(184,153,104,0.3)", color: "#2C2A28" }}
      />
    </div>
  );
}

function StepCustomer({ state, update }) {
  return (
    <div>
      <StepHeader eyebrow="Step 11 · Delivery" title="A few details to send your dream home." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input label="Full name" testId="cust-name" value={state.full_name} onChange={(v) => update("full_name", v)} required />
        <Input label="Email" testId="cust-email" type="email" value={state.email} onChange={(v) => update("email", v)} required />
        <Input label="Mobile phone" testId="cust-phone" value={state.phone} onChange={(v) => update("phone", v)} required />
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: "#4A4744" }}>Fulfillment</label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-testid="fulfillment-delivery-btn"
              onClick={() => update("fulfillment", "delivery")}
              className="px-4 py-3 rounded-xl border transition flex items-center justify-center gap-2 text-sm"
              style={{
                backgroundColor: state.fulfillment === "delivery" ? "#A8BCC8" : "#FFFFFF",
                borderColor: state.fulfillment === "delivery" ? "#A8BCC8" : "rgba(184,153,104,0.3)",
                color: "#2C2A28",
              }}
            >
              <Truck size={16} /> Delivery
            </button>
            <button
              type="button"
              data-testid="fulfillment-pickup-btn"
              onClick={() => update("fulfillment", "pickup")}
              className="px-4 py-3 rounded-xl border transition flex items-center justify-center gap-2 text-sm"
              style={{
                backgroundColor: state.fulfillment === "pickup" ? "#9CAF88" : "#FFFFFF",
                borderColor: state.fulfillment === "pickup" ? "#9CAF88" : "rgba(184,153,104,0.3)",
                color: state.fulfillment === "pickup" ? "#B8C5CD" : "#2C2A28",
              }}
            >
              <Store size={16} /> Pickup
            </button>
          </div>
        </div>
      </div>

      {state.fulfillment === "delivery" ? (
        <div className="mt-4 space-y-4">
          <Input label="Shipping address" testId="cust-address" value={state.shipping_address} onChange={(v) => update("shipping_address", v)} required />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input label="City" testId="cust-city" value={state.shipping_city} onChange={(v) => update("shipping_city", v)} required />
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: "#4A4744" }}>
                State<span style={{ color: "#B89968" }}> *</span>
              </label>
              <select
                data-testid="cust-state"
                value={state.shipping_state}
                onChange={(e) => update("shipping_state", e.target.value)}
                className="w-full bg-white border rounded-xl px-4 py-3 text-sm outline-none transition focus:ring-2"
                style={{ borderColor: "rgba(184,153,104,0.3)", color: "#2C2A28" }}
              >
                <option value="">Select state…</option>
                {US_STATES.map((s) => (
                  <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
                ))}
              </select>
            </div>
            <Input label="ZIP code" testId="cust-zip" value={state.shipping_zip} onChange={(v) => update("shipping_zip", v)} required />
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <label className="block text-sm font-medium mb-2" style={{ color: "#4A4744" }}>Pickup location</label>
          <div className="grid gap-2">
            {PICKUP_LOCATIONS.map((loc) => (
              <button
                key={loc}
                type="button"
                data-testid={`pickup-loc-${PICKUP_LOCATIONS.indexOf(loc)}`}
                onClick={() => update("pickup_location", loc)}
                className="text-left px-4 py-3 rounded-xl border transition text-sm"
                style={{
                  backgroundColor: state.pickup_location === loc ? "#9CAF88" : "#FFFFFF",
                  borderColor: state.pickup_location === loc ? "#9CAF88" : "rgba(184,153,104,0.3)",
                  color: state.pickup_location === loc ? "#B8C5CD" : "#2C2A28",
                }}
              >
                {loc}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4">
        <Input label="Notes or special requests" testId="cust-notes" value={state.notes} onChange={(v) => update("notes", v)} textarea />
      </div>

      {state.fulfillment === "delivery" && <ShippingSummary customer={state} />}

      <div className="mt-8 p-5 rounded-2xl border-l-4" style={{ backgroundColor: "#C5D0D6", borderColor: "#B89968" }}>
        <p className="text-sm leading-relaxed" style={{ color: "#4A4744" }}>
          Your custom gelato will be prepared only after payment is completed. You will receive a secure payment link by email or text message.
        </p>
      </div>
    </div>
  );
}

function ShippingSummary({ customer }) {
  const { shipping_state, shipping_zip } = customer;
  const ship = computeShipping(shipping_state, shipping_zip);

  if (!shipping_state) {
    return (
      <div data-testid="shipping-summary" className="mt-6 p-5 rounded-2xl border" style={{ backgroundColor: "#FFFFFF", borderColor: "rgba(184,153,104,0.3)" }}>
        <div className="eyebrow mb-1">Shipping</div>
        <p className="text-sm" style={{ color: "#75716C" }}>Select your state to see the shipping cost.</p>
      </div>
    );
  }

  if (ship.blocked) {
    return (
      <div data-testid="shipping-blocked" className="mt-6 p-5 rounded-2xl border-l-4" style={{ backgroundColor: "#EFE0E0", borderColor: "#8F5A5A" }}>
        <div className="text-sm font-semibold" style={{ color: "#8F5A5A" }}>Delivery not available</div>
        <p className="mt-1 text-sm" style={{ color: "#7B1E3B" }}>{ship.message}</p>
      </div>
    );
  }

  return (
    <div data-testid="shipping-summary" className="mt-6 p-5 rounded-2xl border-l-4" style={{ backgroundColor: "#F5EFE0", borderColor: "#8E6E3F" }}>
      <div className="flex items-center justify-between">
        <div className="eyebrow">Shipping cost</div>
        <div data-testid="shipping-cost" className="font-serif-display text-2xl" style={{ color: "#2C2A28" }}>${ship.cost}</div>
      </div>
      <p className="mt-1 text-xs" style={{ color: "#75716C" }}>Calculated for {shipping_state}{shipping_zip ? ` · ${shipping_zip}` : ""}. Added to your final payment link.</p>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testId, required, textarea }) {
  const C = textarea ? "textarea" : "input";
  return (
    <div>
      <label className="block text-sm font-medium mb-2" style={{ color: "#4A4744" }}>
        {label}{required && <span style={{ color: "#B89968" }}> *</span>}
      </label>
      <C
        data-testid={testId}
        type={type}
        rows={textarea ? 3 : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border rounded-xl px-4 py-3 text-sm outline-none transition focus:ring-2"
        style={{ borderColor: "rgba(184,153,104,0.3)", color: "#2C2A28" }}
      />
    </div>
  );
}

/* ---------- Confirmation ---------- */
function Confirmation({ data, onHome }) {
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#B8C5CD" }}>
      <Header />
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-2xl w-full text-center"
        >
          <Sparkles size={42} strokeWidth={1.3} className="mx-auto mb-6" style={{ color: "#B89968" }} />
          <h1 data-testid="confirmation-title" className="font-serif-display text-5xl md:text-6xl tracking-tight" style={{ color: "#2C2A28" }}>
            Thank you.
          </h1>
          <p className="mt-6 text-lg leading-relaxed" style={{ color: "#4A4744" }}>
            Your custom gelato request <em>"{data.gelato_name}"</em> has been received. The Santa Chiara team will review your creation and send you a secure payment link. Once payment is completed, your gelato will be prepared with love and shipped within 48 hours or made ready for pickup.
          </p>
          <div className="mt-8 inline-block rounded-2xl border px-6 py-4" style={{ borderColor: "rgba(184,153,104,0.25)" }}>
            <div className="eyebrow">Request ID</div>
            <div className="font-mono text-sm mt-1" style={{ color: "#2C2A28" }}>{data.id}</div>
          </div>
          <div className="mt-10">
            <button
              data-testid="back-home-btn"
              onClick={onHome}
              className="px-8 py-3.5 rounded-full font-medium transition"
              style={{ backgroundColor: "#2C2A28", color: "#B8C5CD" }}
            >
              Return to Santa Chiara
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
